import { formatDate } from '@angular/common';
import { defaultLocaleId } from '@shared/localization/localization.constants';
import { isDefined } from '@stillmandigital/common';
import BigNumber from 'bignumber.js';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(duration);

type DateParamType = Date | dayjs.Dayjs | string | number | undefined;

export type SupportedTimezone =
  | 'America/New_York'
  | 'Canada/Mountain'
  | 'US/Central'
  | 'UTC'
  | 'CET'
  | 'EET'
  | 'PST8PDT';

export const defaultTimezone: SupportedTimezone = 'America/New_York';

const timezoneOptions: SupportedTimezone[] = [
  'America/New_York',
  'Canada/Mountain',
  'US/Central',
  'UTC',
  'CET',
  'EET',
  'PST8PDT',
];

export function isTimezoneSupportedByRuntime(timezone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

export const supportedTimezones: SupportedTimezone[] = timezoneOptions.filter(isTimezoneSupportedByRuntime);

export const FORMAT_DATE_TIME_WITH_MS = 'YYYY-MM-DDTHH:mm:ss.SSS';

export function convertFromUTC(value: DateParamType, timezone: SupportedTimezone): string {
  // Dayjs mis-parses fractional seconds with fewer than 3 digits when no timezone is specified.
  // Appending Z ensures correct parsing. https://github.com/iamkun/dayjs/issues/1923
  const normalized = typeof value === 'string' && !/Z|[+-]\d{2}:\d{2}$/.test(value) ? value + 'Z' : value;
  return dayjs.utc(normalized).tz(timezone).format(FORMAT_DATE_TIME_WITH_MS);
}

export function formatToLocaleDateString(value: DateParamType, timezone: SupportedTimezone, format = 'medium') {
  if (!isDefined(value)) {
    return '';
  }

  const date = convertFromUTC(value, timezone);

  return formatDate(date, format, defaultLocaleId);
}

export function toAPIDateTimeStringFormat(value: DateParamType, timezone = defaultTimezone) {
  return dayjs.utc(dayjs.tz(value, timezone)).format(FORMAT_DATE_TIME_WITH_MS);
}

export function addMs(value: DateParamType, ms: number, timezone = defaultTimezone) {
  return dayjs.tz(value, timezone).add(ms, 'ms').format(FORMAT_DATE_TIME_WITH_MS);
}

export function timeTo(from: DateParamType, to: DateParamType) {
  // Dayjs mis-parses fractional seconds with fewer than 3 digits (e.g. ".81" → 81ms instead of 810ms).
  // Appending Z and using utc() forces correct parsing. https://github.com/iamkun/dayjs/issues/1923
  const normalize = (v: DateParamType) => (typeof v === 'string' && !/Z|[+-]\d{2}:\d{2}$/.test(v) ? v + 'Z' : v);
  const diff = dayjs.utc(normalize(to)).diff(dayjs.utc(normalize(from)), 'ms');

  return formatDuration(diff);
}

const timeUnitMap: { [key: number]: string } = {
  0: 'm',
  1: 's',
  2: 'ms',
};

export function formatDuration(value: duration.Duration | number | string) {
  const duration = typeof value === 'object' ? value : dayjs.duration(Number(value), 'ms');

  if (duration.valueOf() === 0) {
    return '0ms';
  }

  if (duration.get('hours')) {
    return duration.humanize().replaceAll('an ', '1 ');
  }

  return [duration.get('minutes'), duration.get('seconds'), duration.get('ms')]
    .map((value, index) => (value ? `${value}${timeUnitMap[index]}` : undefined))
    .filter(Boolean)
    .join(' ');
}

export function toSeconds(nanoseconds?: number) {
  const divisor = new BigNumber(1000).pow(3).toNumber();
  return nanoseconds ? new BigNumber(nanoseconds).div(divisor).toNumber() : 0;
}
